import React, { useState, useEffect, useCallback } from 'react';
import { Plane, Save, Trash2, Loader2, Ticket } from 'lucide-react';
import { mileageCardService } from '../services/mileageCardService';
import {
  MileageCard,
  MileageUsageWithCard,
  MileageBalance,
} from '../interfaces/MileageCard';

interface Props {
  checkoutId: string;
  /** true quando a viagem é ida e volta (round_trip) */
  isRoundTrip: boolean;
  /**
   * Companhia aérea do voo (ex: LATAM, GOL, Azul). Quando informada, os
   * cartões listados são filtrados para o programa de fidelidade dessa
   * companhia.
   */
  airline?: string;
}

const PROGRAM_LABEL: Record<string, string> = {
  azul: 'Azul',
  latam: 'LATAM',
  smiles: 'Smiles',
  livelo: 'Livelo',
  tap: 'TAP',
};

const formatMiles = (n: number) => Number(n).toLocaleString('pt-BR');

/**
 * Seção de "Emissão por Milhas" exibida em /emissions/:id.
 * Permite marcar que a ida e/ou a volta foi emitida por milhas e qual
 * cartão de milhas foi usado em cada trecho.
 */
const MileageEmissionSection: React.FC<Props> = ({
  checkoutId,
  isRoundTrip,
  airline,
}) => {
  const [cards, setCards] = useState<MileageCard[]>([]);
  const [balances, setBalances] = useState<Record<string, MileageBalance>>({});
  const [usages, setUsages] = useState<MileageUsageWithCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // estado por trecho
  const [outboundOn, setOutboundOn] = useState(false);
  const [outboundCard, setOutboundCard] = useState('');
  const [outboundMiles, setOutboundMiles] = useState('');
  const [savingOutbound, setSavingOutbound] = useState(false);

  const [inboundOn, setInboundOn] = useState(false);
  const [inboundCard, setInboundCard] = useState('');
  const [inboundMiles, setInboundMiles] = useState('');
  const [savingInbound, setSavingInbound] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cardList, usageList] = await Promise.all([
        mileageCardService.getCards(airline),
        mileageCardService.getUsagesByCheckout(checkoutId),
      ]);
      setCards(cardList);
      setUsages(usageList);

      // carrega saldos dos cartões ativos para exibir no selector
      const bals = await Promise.all(
        cardList.map((c) =>
          mileageCardService
            .getBalance(c.id)
            .then((b) => [c.id, b] as const)
            .catch(() => null),
        ),
      );
      const map: Record<string, MileageBalance> = {};
      bals.forEach((entry) => {
        if (entry) map[entry[0]] = entry[1];
      });
      setBalances(map);
    } catch {
      setError('Erro ao carregar dados de milhas.');
    } finally {
      setLoading(false);
    }
  }, [checkoutId, airline]);

  useEffect(() => {
    load();
  }, [load]);

  const outboundUsage = usages.find((u) => u.usageLeg === 'outbound');
  const inboundUsage = usages.find((u) => u.usageLeg === 'inbound');

  const handleAttach = async (leg: 'outbound' | 'inbound') => {
    const cardId = leg === 'outbound' ? outboundCard : inboundCard;
    const miles = leg === 'outbound' ? outboundMiles : inboundMiles;
    const setSaving =
      leg === 'outbound' ? setSavingOutbound : setSavingInbound;

    if (!cardId || !miles) {
      setError('Selecione o cartão e informe a quantidade de milhas.');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await mileageCardService.attachUsageToCheckout(checkoutId, {
        cardId,
        milesUsed: Number(miles),
        usageLeg: leg,
        isRoundTrip,
      });
      if (leg === 'outbound') {
        setOutboundOn(false);
        setOutboundCard('');
        setOutboundMiles('');
      } else {
        setInboundOn(false);
        setInboundCard('');
        setInboundMiles('');
      }
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Erro ao atrelar milhas (verifique o saldo do cartão).',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDetach = async (usageId: string) => {
    if (!window.confirm('Remover o atrelamento de milhas deste trecho?')) return;
    setError(null);
    try {
      await mileageCardService.detachUsageFromCheckout(usageId);
      await load();
    } catch {
      setError('Erro ao remover atrelamento de milhas.');
    }
  };

  const activeCards = cards.filter((c) => c.is_active);

  const renderCardOption = (c: MileageCard) => {
    const bal = balances[c.id];
    const balText = bal ? ` — saldo ${formatMiles(bal.balance)}` : '';
    return (
      <option key={c.id} value={c.id}>
        {PROGRAM_LABEL[c.program] ?? c.program} · {c.name}
        {balText}
      </option>
    );
  };

  const renderLeg = (
    leg: 'outbound' | 'inbound',
    label: string,
    usage: MileageUsageWithCard | undefined,
    on: boolean,
    setOn: (v: boolean) => void,
    cardVal: string,
    setCardVal: (v: string) => void,
    milesVal: string,
    setMilesVal: (v: string) => void,
    saving: boolean,
  ) => (
    <div
      style={{
        border: '1px solid #e8e8e8',
        borderRadius: 'var(--br-5)',
        padding: '14px',
        marginBottom: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <strong style={{ fontSize: 'var(--font-size-14)' }}>
          <Plane size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          {label}
        </strong>
        {usage ? (
          <span
            style={{
              background: '#f6ffed',
              color: '#389e0d',
              padding: '2px 10px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            Emitido por milhas
          </span>
        ) : (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={(e) => setOn(e.target.checked)}
            />
            Emitir por milhas
          </label>
        )}
      </div>

      {/* Já atrelado */}
      {usage && (
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fafafa',
            borderRadius: 'var(--br-5)',
            padding: '10px 12px',
            fontSize: '13px',
          }}
        >
          <span>
            <strong>
              {usage.card
                ? `${PROGRAM_LABEL[usage.card.program] ?? usage.card.program} · ${usage.card.name}`
                : 'Cartão'}
            </strong>{' '}
            — {formatMiles(usage.milesUsed)} milhas
          </span>
          <button
            onClick={() => handleDetach(usage.id)}
            className="btn btn-sm"
            title="Remover atrelamento"
            style={{
              border: '1px solid #ffccc7',
              background: '#fff',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={13} color="#cf1322" />
          </button>
        </div>
      )}

      {/* Form de atrelamento */}
      {!usage && on && (
        <div style={{ marginTop: '12px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '10px',
              marginBottom: '10px',
            }}
          >
            <div className="form-group">
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-14)',
                }}
              >
                Cartão de Milhas
              </label>
              <select
                value={cardVal}
                onChange={(e) => setCardVal(e.target.value)}
                className="form-control"
                disabled={saving}
              >
                <option value="">Selecione...</option>
                {activeCards.map(renderCardOption)}
              </select>
            </div>
            <div className="form-group">
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-14)',
                }}
              >
                Milhas usadas
              </label>
              <input
                type="number"
                min={1}
                value={milesVal}
                onChange={(e) => setMilesVal(e.target.value)}
                className="form-control"
                placeholder="ex: 25000"
                disabled={saving}
              />
            </div>
          </div>
          <button
            onClick={() => handleAttach(leg)}
            disabled={saving}
            className="btn btn-success btn-sm"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Salvando...
              </>
            ) : (
              <>
                <Save size={14} /> Atrelar milhas
              </>
            )}
          </button>
        </div>
      )}

      {activeCards.length === 0 && !usage && on && (
        <p style={{ fontSize: '12px', color: '#cf1322', marginTop: '8px' }}>
          {airline
            ? `Nenhum cartão de milhas de ${airline} cadastrado. Cadastre em "Cartões de Milhas".`
            : 'Nenhum cartão de milhas ativo cadastrado. Cadastre em "Cartões de Milhas".'}
        </p>
      )}
    </div>
  );

  return (
    <div className="card">
      <h3
        style={{
          margin: '0 0 16px 0',
          fontSize: 'var(--font-size-18)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Ticket size={18} />
        Emissão por Milhas
      </h3>

      {error && (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 'var(--br-5)',
            padding: '10px 14px',
            marginBottom: '12px',
            color: '#a8071a',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '12px', color: '#888', fontSize: '13px' }}>
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : (
        <>
          {renderLeg(
            'outbound',
            'Ida',
            outboundUsage,
            outboundOn,
            setOutboundOn,
            outboundCard,
            setOutboundCard,
            outboundMiles,
            setOutboundMiles,
            savingOutbound,
          )}

          {isRoundTrip &&
            renderLeg(
              'inbound',
              'Volta',
              inboundUsage,
              inboundOn,
              setInboundOn,
              inboundCard,
              setInboundCard,
              inboundMiles,
              setInboundMiles,
              savingInbound,
            )}
        </>
      )}
    </div>
  );
};

export default MileageEmissionSection;
